import { registerUploadFilesApi, getUploadFileUrlsApi, updateUploadFileStatusApi } from './apiServices';
import { updateLiveUpload, LIVE_STATUS } from './liveUploads';

// Files are registered and signed one batch at a time so signed URLs are fresh when used
// and a 10k-file upload never fires thousands of API calls at once
const BATCH_SIZE = 100;
const UPLOAD_CONCURRENCY = 6;
const MAX_ATTEMPTS = 3;
// Refresh a signed URL this long before its expires_at rather than letting S3 reject it
const URL_EXPIRY_MARGIN_MS = 60 * 1000;
// Register statuses meaning the file is already uploaded in this session
const DONE_STATUSES = ['COMPLETED', 'UPLOADED'];
// Byte progress fires many times a second; publish to the UI at most this often
const PUBLISH_INTERVAL_MS = 300;
// Speed is smoothed over samples at least this far apart so it doesn't jitter
const SPEED_SAMPLE_MS = 1000;

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Exponential backoff with jitter: ~1s, ~2s, ~4s
const withRetry = async (task) => {
  let lastError;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    try {
      return await task(attempt);
    } catch (error) {
      lastError = error;
      if (attempt < MAX_ATTEMPTS - 1) await sleep(1000 * 2 ** attempt + Math.random() * 300);
    }
  }
  throw lastError;
};

const isExpiring = (signed) => !!signed.expiresAt && Date.now() > signed.expiresAt - URL_EXPIRY_MARGIN_MS;

// XHR rather than fetch because only XHR reports upload progress. It also never carries the
// app's Authorization header, which S3 rejects on presigned requests. The URL signs
// content-type, so the server-provided headers are sent unchanged.
const putToStorage = (signed, file, onBytes) => new Promise((resolve, reject) => {
  const xhr = new XMLHttpRequest();
  xhr.open(signed.method || 'PUT', signed.url);
  const headers = signed.headers || { 'Content-Type': file.type || 'application/octet-stream' };
  Object.keys(headers).forEach(name => xhr.setRequestHeader(name, headers[name]));
  xhr.upload.onprogress = (evt) => onBytes(evt.loaded);
  xhr.onload = () => {
    if (xhr.status >= 200 && xhr.status < 300) {
      resolve();
    } else {
      const error = new Error(`Storage returned HTTP ${xhr.status}`);
      error.status = xhr.status;
      reject(error);
    }
  };
  xhr.onerror = () => reject(new Error('Network error while uploading'));
  xhr.send(file);
});

const runPool = async (tasks, concurrency) => {
  let cursor = 0;
  const workers = Array.from({ length: Math.min(concurrency, tasks.length) }, async () => {
    while (cursor < tasks.length) {
      const task = tasks[cursor++];
      await task();
    }
  });
  await Promise.all(workers);
};

const toFileMeta = ({ file, relativePath }) => ({
  name: file.name,
  size: file.size,
  type: file.type || 'application/octet-stream',
  path: relativePath,
});

/**
 * Uploads items ({ file, relativePath }) into an existing upload session.
 * Never rejects: per-file failures are collected and returned so one bad file
 * doesn't stop the rest of the upload.
 */
export const runStreamUpload = async ({ assetType, assetSlug, uploadId, items, onProgress }) => {
  const api = { assetType, assetSlug, uploadId };
  const progress = {
    totalFiles: items.length,
    totalBytes: items.reduce((sum, item) => sum + item.file.size, 0),
    completedFiles: 0,
    failedFiles: 0,
    skippedFiles: 0,
    uploadedBytes: 0,
  };
  const failures = [];
  const skipped = [];
  // Bytes sent so far for files still in flight, so the bar moves within large files
  const inFlight = new Map();
  const startedAt = Date.now();
  let speed = 0;
  let speedSample = { at: startedAt, bytes: 0 };
  let lastPublish = 0;

  const snapshot = () => {
    let inFlightBytes = 0;
    inFlight.forEach(bytes => { inFlightBytes += bytes; });
    return { ...progress, uploadedBytes: progress.uploadedBytes + inFlightBytes };
  };
  const publish = (force = false) => {
    const now = Date.now();
    if (!force && now - lastPublish < PUBLISH_INTERVAL_MS) return;
    lastPublish = now;
    const current = snapshot();
    if (now - speedSample.at >= SPEED_SAMPLE_MS) {
      const instant = Math.max(0, current.uploadedBytes - speedSample.bytes) / ((now - speedSample.at) / 1000);
      speed = speed ? speed * 0.7 + instant * 0.3 : instant;
      speedSample = { at: now, bytes: current.uploadedBytes };
    }
    updateLiveUpload(uploadId, { ...current, speed, status: LIVE_STATUS.UPLOADING, startedAt });
  };
  const report = () => {
    publish();
    if (onProgress) onProgress(snapshot());
  };
  const fail = (item, reason) => {
    progress.failedFiles += 1;
    failures.push({ path: item.relativePath, error: reason || 'Upload failed' });
  };
  const complete = (item) => {
    progress.completedFiles += 1;
    progress.uploadedBytes += item.file.size;
  };

  publish(true);
  try {
    for (let offset = 0; offset < items.length; offset += BATCH_SIZE) {
      const batch = items.slice(offset, offset + BATCH_SIZE);

      let registered;
      try {
        registered = await withRetry(() => registerUploadFilesApi({ ...api, files: batch.map(toFileMeta) }));
      } catch (error) {
        batch.forEach(item => fail(item, error.message));
        report();
        continue;
      }

      // Match server rows back to local files by path, or by name when it is unique in the batch
      const byPath = new Map(registered.files.filter(row => row.path).map(row => [row.path, row]));
      const nameCounts = batch.reduce((counts, item) => ({ ...counts, [item.file.name]: (counts[item.file.name] || 0) + 1 }), {});
      const byName = new Map(registered.files.filter(row => row.name && nameCounts[row.name] === 1).map(row => [row.name, row]));
      const rejected = new Map(registered.rejected.filter(entry => entry.id).map(entry => [entry.id, entry.reason]));

      const toUpload = [];
      batch.forEach(item => {
        const row = byPath.get(item.relativePath) || byName.get(item.file.name);
        if (row && row.fileId) {
          if (DONE_STATUSES.includes(row.status)) complete(item);
          else toUpload.push({ item, fileId: row.fileId });
        } else {
          const reason = rejected.get(item.relativePath) || rejected.get(item.file.name);
          fail(item, reason ? `Rejected: ${reason}` : rejected.size ? 'Rejected by server' : 'File was not registered');
        }
      });
      report();
      if (!toUpload.length) continue;

      let signedBatch;
      try {
        signedBatch = await withRetry(() => getUploadFileUrlsApi({ ...api, fileIds: toUpload.map(pair => pair.fileId) }));
      } catch (error) {
        toUpload.forEach(pair => fail(pair.item, error.message));
        report();
        continue;
      }
      const skippedIds = new Map(signedBatch.skipped.filter(entry => entry.id).map(entry => [entry.id, entry.reason]));

      await runPool(toUpload.map(({ item, fileId }) => async () => {
        if (skippedIds.has(fileId)) {
          progress.skippedFiles += 1;
          skipped.push({ path: item.relativePath, reason: skippedIds.get(fileId) || 'Skipped by server' });
          report();
          return;
        }
        let signed = signedBatch.urls[fileId];
        try {
          await withRetry(async () => {
            // Missing, about to expire, or rejected with 403 (expired): fetch a fresh URL first
            if (!signed || signed.expired || isExpiring(signed)) {
              const fresh = await getUploadFileUrlsApi({ ...api, fileIds: [fileId] });
              signed = fresh.urls[fileId];
              if (!signed) throw new Error('No upload URL returned');
            }
            inFlight.set(fileId, 0);
            try {
              await putToStorage(signed, item.file, (bytes) => {
                inFlight.set(fileId, bytes);
                report();
              });
            } catch (error) {
              inFlight.set(fileId, 0);
              report();
              if (error.status === 403) signed = { ...signed, expired: true };
              throw error;
            }
          });
        } catch (error) {
          inFlight.delete(fileId);
          fail(item, error.message);
          // Best effort: the file already counts as failed locally even if this report is lost
          updateUploadFileStatusApi({ ...api, fileId, status: 'FAILED', uploadedBytes: 0, errorMessage: error.message }).catch(() => {});
          report();
          return;
        }
        // The bytes are in storage, but the file only appears in the folder once the server
        // marks it completed, so it isn't counted as done until that call succeeds
        try {
          await withRetry(() => updateUploadFileStatusApi({ ...api, fileId, status: 'COMPLETED', uploadedBytes: item.file.size }));
          inFlight.delete(fileId);
          complete(item);
        } catch (error) {
          inFlight.delete(fileId);
          fail(item, `Uploaded, but the server could not be updated: ${error.message}`);
        }
        report();
      }), UPLOAD_CONCURRENCY);
    }
  } finally {
    updateLiveUpload(uploadId, { ...snapshot(), speed: 0, status: LIVE_STATUS.DONE, startedAt, finishedAt: Date.now() });
  }
  return { ...progress, failures, skipped };
};
